import React, { useCallback, useEffect, useRef, useState } from 'react';

import { saveAs } from 'file-saver';
import {
  MDBBtn,
  MDBCol,
  MDBContainer,
  MDBIcon,
  MDBRow,
  MDBSpinner,
} from 'mdbreact';
import Select from 'react-select';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import api from '../../../services/api';
import { getData, getPerfil } from '../../../services/auth';
import resolverMensagemErro from '../../../utils/resolverMensagemErro';
import ehCelular from '../Abordagens/Imprimir/relatorio/dispositivo';
import nomeDoEmissor from '../Abordagens/Imprimir/relatorio/emissor';
import { dataHora } from '../Abordagens/Imprimir/relatorio/formatadores';
import gerarPdfDia, { nomeDoArquivoDia } from './relatorio/gerarPdfDia';

// getData() estoura se o token estiver ausente ou corrompido; o rodapé cai
// para o perfil salvo nesse caso, não deixa de sair (mesmo helper de
// Abordagens/Imprimir/index.js).
const dadosDoToken = () => {
  try {
    return getData();
  } catch (erro) {
    return null;
  }
};

// Hoje em Brasília, no formato 'AAAA-MM-DD' do seletor e do parâmetro `data`
// da API — mesma fórmula do fallback do backend (RelatorioDiaController), só
// que aqui precisa existir ANTES da requisição, para preencher o campo.
const hojeEmBrasilia = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

// Mesmo truque de `soData` em montaDocumentoDia.js: `data` é uma data pura
// ('AAAA-MM-DD'), sem horário — fixa meio-dia em Brasília antes de formatar
// para nunca escorregar de dia por causa de fuso.
const dataDoCabecalho = iso => dataHora(`${iso}T12:00:00.000-03:00`).split(' ')[0];

// Alvo de toque: 48px é o mínimo confortável para quem opera de pé, em posto
// de estrada, às vezes de luva. O `form-control` do Bootstrap nasce com 38px
// e o `react-select` com 38px — os dois sobem juntos, senão o par de campos
// fica desalinhado no desktop.
const ALTURA_CAMPO = 48;
const ESTILO_SELECT = {
  control: base => ({ ...base, minHeight: ALTURA_CAMPO }),
};

const CLASSE_ROTULO = 'd-flex align-items-center mb-1 font-weight-bolder';
const ESTILO_ROTULO = { color: '#42565f', fontSize: '0.95rem' };

// Numerar só faz sentido porque AQUI a ordem é informação: quem tem seletor
// precisa escolher o posto ANTES de o botão liberar. Quem é de posto folha
// não vê seletor — e não vê número nenhum, porque não há sequência: é um
// campo e um botão.
const passo = numero => (
  <span
    aria-hidden="true"
    className="d-inline-flex align-items-center justify-content-center mr-2"
    style={{
      width: 22,
      height: 22,
      borderRadius: '50%',
      background: '#e3e8ec',
      color: '#42565f',
      fontSize: '0.75rem',
      flex: '0 0 auto',
    }}
  >
    {numero}
  </span>
);

/**
 * Relatório do dia em PDF (consolidado do posto do usuário).
 *
 * Diferente do relatório de abordagem (que abre já carregando um id da URL),
 * aqui o caminho principal é UM BOTÃO: o público-alvo não tem familiaridade
 * com o sistema, então nada de formulário de filtros.
 *
 * A tela é usada mais no celular do que no computador, por gente de pé em
 * posto de estrada. Daí o desenho: uma coluna só, na ordem das decisões
 * (posto, dia, gerar), o botão verde ocupando a largura da tela como única
 * cor forte, e todo o resto — Voltar, Imprimir, Baixar — fora do caminho até
 * ter função.
 */
export default function RelatorioDia() {
  const history = useHistory();

  const [data, setData] = useState(hojeEmBrasilia());
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [dados, setDados] = useState(null);
  const [arquivo, setArquivo] = useState({ blob: null, url: null });
  const iframe = useRef(null);
  // no celular não há preview: o Chrome no Android não renderiza PDF em iframe
  const celular = useRef(ehCelular(typeof navigator === 'undefined' ? null : navigator));

  // Escolha de posto (Task 11 / D7): quem é de posto folha só tem a própria
  // gerência em `/filtra-gerencia` e nunca vê o seletor — o clique único
  // continua intacto. Quem é da mãe (própria + subordinadas) escolhe entre
  // "Todos os postos" e cada posto do escopo.
  const [listaPostos, setListaPostos] = useState([]);
  const [postoSelecionado, setPostoSelecionado] = useState(null);
  // até isto virar true o botão fica desabilitado — clicar antes da resposta
  // de /filtra-gerencia manda a chamada SEM gerencia_id (o seletor ainda nem
  // existe), e pra quem é da mãe isso é exatamente o relatório vazio que
  // esta task existe para matar, além de uma linha de emissão com 0
  // abordagens gravada à toa.
  const [gerenciasCarregadas, setGerenciasCarregadas] = useState(false);
  const mostrarSeletorPosto = listaPostos.length > 1;
  // quem tem seletor só gera depois de escolher um posto (ou "Todos os
  // postos", que também é uma escolha explícita) — sem isso o clique cai sem
  // `gerencia_id` e o backend usa o posto do próprio usuário (a mãe), que é o
  // relatório vazio que essa funcionalidade existe para matar. Quem é de
  // posto folha (sem seletor) não é afetado por essa trava.
  const podeGerar = !mostrarSeletorPosto || !!postoSelecionado;

  useEffect(() => {
    api
      .get('/filtra-gerencia')
      .then(resposta => {
        // o endpoint devolve inativas também (ativo: true fica comentado lá
        // de propósito, não é para mexer) — não faz sentido oferecer posto
        // desativado no seletor.
        const gerencias = (resposta.data || []).filter(g => g.ativo !== false);
        if (gerencias.length <= 1) return; // sem seletor: nada a montar
        const opcoes = [
          // mesmo texto da etiqueta da trilha (RelatorioDiaEmissoes): quem
          // escolhe aqui tem de reconhecer o que ficou registrado lá. E o
          // alcance é o escopo de quem pede, não a rede inteira.
          { value: 'todos', label: 'Posto e subordinados' },
          ...gerencias.map(g => ({ value: String(g.id), label: g.sigla || g.nome })),
        ];
        setListaPostos(opcoes);
        // sem pré-seleção: o desenho rejeitou explicitamente abrir a tela já
        // em "Todos os postos" (o agregado máximo em um clique). Quem tem
        // seletor ESCOLHE — o botão fica desabilitado até isso acontecer
        // (ver `podeGerar` abaixo).
      })
      .catch(() => {
        // sem a lista, ninguém vê seletor — quem é da mãe cairia no relatório
        // vazio do próprio posto sem entender por quê. Melhor avisar do que
        // deixar a tela em silêncio.
        toast.error('Não foi possível carregar a lista de postos. Mostrando só o seu posto padrão.');
      })
      .finally(() => setGerenciasCarregadas(true));
  }, []);

  const gerar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const params = { data };
      // gerencia_id só viaja quando há seletor — de posto folha (clique
      // único) o contrato de sempre não muda em nada.
      if (mostrarSeletorPosto && postoSelecionado) params.gerencia_id = postoSelecionado.value;

      const resposta = await api.get('/relatorio-dia', { params });
      const payload = resposta.data;

      const blob = await gerarPdfDia(payload, {
        emitidoPor: nomeDoEmissor(dadosDoToken(), getPerfil()),
      });

      setDados(payload);
      setArquivo({ blob, url: URL.createObjectURL(blob) });

      // celular: entrega o arquivo assim que ele fica pronto, sem esperar toque
      if (celular.current) saveAs(blob, nomeDoArquivoDia(payload.posto.sigla, payload.data));
    } catch (err) {
      // 403 tem duas origens: sem acesso NENHUM ao relatório do dia (gate,
      // sem seletor envolvido — mensagem própria, mais clara que a genérica)
      // ou o posto escolhido no seletor está fora do escopo do usuário (a
      // mensagem específica vem do backend, e precisa aparecer como veio).
      const status = err && err.response && err.response.status;
      const mensagem =
        status === 403 && !mostrarSeletorPosto
          ? 'Você não tem acesso ao relatório do dia.'
          : resolverMensagemErro(err);
      setErro(mensagem);
      toast.error(mensagem);
      // uma reemissão que falha (ex.: troca de data, segunda chamada dá 403)
      // não pode deixar o título/totais da geração ANTERIOR na tela — parece
      // sucesso parcial quando na verdade essa chamada falhou por completo.
      setDados(null);
    } finally {
      setCarregando(false);
    }
  }, [data, mostrarSeletorPosto, postoSelecionado]);

  // o objectURL segura o arquivo na memória do navegador até ser revogado —
  // roda tanto ao trocar de data (novo blob) quanto ao desmontar a tela.
  useEffect(() => () => arquivo.url && URL.revokeObjectURL(arquivo.url), [arquivo.url]);

  const baixar = () => dados && saveAs(arquivo.blob, nomeDoArquivoDia(dados.posto.sigla, dados.data));

  const imprimir = () => {
    if (!iframe.current || !iframe.current.contentWindow) return;
    iframe.current.contentWindow.focus();
    iframe.current.contentWindow.print();
  };

  // `dados` já é limpo no catch de `gerar`, mas o título e a faixa de totais
  // ficam condicionados a `!erro` também — por garantia, para que uma futura
  // mudança em `gerar` não reintroduza o estado obsoleto (título/totais da
  // geração anterior sobrevivendo a uma reemissão que falhou).
  const titulo =
    dados && !erro
      ? `Relatório do dia — ${dados.posto.sigla || dados.posto.nome}${
          dados.abrangencia === 'todos' ? ' · TODOS OS POSTOS' : ''
        } · ${dataDoCabecalho(dados.data)}`
      : 'Relatório do dia';

  return (
    <MDBContainer className="py-3">
      <MDBRow between className="align-items-center mb-3">
        <MDBCol size="12" md="6">
          <h5 className="font-weight-bolder mb-0">{titulo}</h5>
        </MDBCol>
        {/*
          Barra de ações só do desktop (`d-none d-md-block`). No celular ela
          aparecia ACIMA do botão que é o motivo da tela existir, competindo
          com ele e quebrando em duas linhas desalinhadas; lá o "Voltar" foi
          para o rodapé e o "Baixar PDF" mora no cartão de download.

          Imprimir e Baixar só entram DEPOIS que o documento existe. Antes
          eles ficavam visíveis e desabilitados: ocupavam espaço, não faziam
          nada e passavam a impressão de tela quebrada.
        */}
        <MDBCol size="12" md="6" className="text-md-right d-none d-md-block">
          <MDBBtn color="mdb-color" size="sm" onClick={() => history.goBack()}>
            <MDBIcon icon="arrow-left" className="mr-2" />
            Voltar
          </MDBBtn>
          {/* imprimir depende do iframe, que no celular não existe — lá quem
              imprime é o app que abre o PDF baixado. `!erro` também trava os
              dois: uma reemissão que falha (troca de data, segunda chamada
              dá 500) zera `dados` mas não `arquivo` — sem essa checagem os
              botões ficavam visíveis e mortos (Baixar dependia de `dados`,
              que já é `null`; Imprimir, do iframe, que desmontou junto com
              o estado de erro), o mesmo antipadrão que este redesenho matou
              na primeira dobra. */}
          {!celular.current && !erro && arquivo.blob && (
            <MDBBtn color="mdb-color" size="sm" onClick={imprimir}>
              <MDBIcon icon="print" className="mr-2" />
              Imprimir
            </MDBBtn>
          )}
          {!celular.current && !erro && arquivo.blob && (
            <MDBBtn color="success" size="sm" onClick={baixar}>
              <MDBIcon icon="file-pdf" className="mr-2" />
              Baixar PDF
            </MDBBtn>
          )}
        </MDBCol>
      </MDBRow>

      {/*
        A ordem da tela é a ordem das decisões: ONDE, QUANDO e só então
        GERAR. Antes o botão vinha primeiro e os campos depois — quem não
        conhece o sistema clicava no botão sem ter escolhido o posto e batia
        no botão desabilitado sem entender o porquê.
      */}
      <MDBRow className="align-items-start mb-3">
        {mostrarSeletorPosto && (
          <MDBCol size="12" md="4" className="mb-3 mb-md-0">
            <label className={CLASSE_ROTULO} style={ESTILO_ROTULO} htmlFor="posto-relatorio-dia-campo">
              {passo(1)}
              Posto
            </label>
            <Select
              id="posto-relatorio-dia"
              inputId="posto-relatorio-dia-campo"
              placeholder="Escolha o posto"
              options={listaPostos}
              value={postoSelecionado}
              isDisabled={carregando}
              onChange={e => setPostoSelecionado(e)}
              styles={ESTILO_SELECT}
            />
          </MDBCol>
        )}
        <MDBCol size="12" md="3" className="mb-3 mb-md-0">
          <label className={CLASSE_ROTULO} style={ESTILO_ROTULO} htmlFor="dia-relatorio-dia">
            {mostrarSeletorPosto && passo(2)}
            Dia
          </label>
          {/* campo nativo, e não o input do MDB: o rótulo flutuante fica por
              cima do valor num `type="date"` e no celular não dava para ler
              a data escolhida. Altura casada com a do seletor de posto. */}
          <input
            id="dia-relatorio-dia"
            className="form-control"
            style={{ height: ALTURA_CAMPO }}
            type="date"
            value={data}
            max={hojeEmBrasilia()}
            disabled={carregando}
            onChange={e => setData(e.target.value)}
          />
          {data === hojeEmBrasilia() && (
            <div className="small text-muted mt-1">Já vem com o dia de hoje.</div>
          )}
        </MDBCol>
        <MDBCol size="12" md="3">
          {/* espaçador: alinha o botão com os controles, não com os rótulos */}
          <label className={`${CLASSE_ROTULO} d-none d-md-flex`} style={ESTILO_ROTULO}>
            &nbsp;
          </label>
          {/* `btn-block`: no celular o alvo é a largura da tela inteira —
              quem usa isto está de pé, com pressa, às vezes de luva.
              `px-2` porque o padrão do MDB (2.14rem de cada lado) fazia o
              rótulo quebrar em duas linhas dentro da coluna no desktop. */}
          <MDBBtn
            color="success"
            disabled={carregando || !gerenciasCarregadas || !podeGerar}
            onClick={gerar}
            className="btn-block m-0 py-3 px-2"
          >
            <MDBIcon icon="file-alt" className="mr-2" />
            {carregando ? 'Gerando…' : 'Gerar relatório do dia'}
          </MDBBtn>
          {/* botão desabilitado sem explicação é o que mais gera chamado:
              diz o que falta em vez de deixar adivinhar. */}
          {gerenciasCarregadas && !podeGerar && (
            <div className="small text-muted mt-2 text-center text-md-left">
              Escolha o posto para liberar o botão.
            </div>
          )}
        </MDBCol>
      </MDBRow>

      {dados && !erro && (
        <MDBRow className="mb-4">
          {[
            ['Abordagens', dados.totais.abordagens],
            ['Ocorrências', dados.totais.ocorrencias],
            ['Fiscais', dados.totais.fiscais],
            ['Policiais', dados.totais.policiais],
            ['Autuados', dados.totais.autuados],
            ['Itens', dados.totais.itens],
          ].map(([rotulo, valor]) => (
            <MDBCol key={rotulo} size="4" md="2" className="text-center mb-2">
              <div style={{ fontSize: '0.7rem', color: '#6b7c88', letterSpacing: '0.05em' }}>
                {rotulo.toUpperCase()}
              </div>
              <div className="font-weight-bolder" style={{ fontSize: '1.3rem' }}>
                {valor}
              </div>
            </MDBCol>
          ))}
        </MDBRow>
      )}

      {carregando && (
        <MDBRow center className="my-5 align-items-center">
          <MDBSpinner small />
          <span className="ml-2 font-weight-bolder">Gerando o relatório…</span>
        </MDBRow>
      )}

      {!carregando && erro && (
        <MDBRow center className="my-5">
          <MDBCol size="12" md="8" className="text-center">
            <p className="font-weight-bolder text-danger">{erro}</p>
            <MDBBtn color="mdb-color" onClick={gerar} className="btn-block m-0">
              Tentar de novo
            </MDBBtn>
          </MDBCol>
        </MDBRow>
      )}

      {!carregando && !erro && arquivo.url && !celular.current && (
        <iframe
          ref={iframe}
          title="Relatório do dia"
          src={arquivo.url}
          style={{ width: '100%', height: '80vh', border: '1px solid #d4dce2' }}
        />
      )}

      {!carregando && !erro && arquivo.url && celular.current && dados && (
        <MDBRow center className="my-4">
          <MDBCol size="12" md="8" className="text-center">
            <MDBIcon icon="file-pdf" size="3x" className="mb-3" style={{ color: '#6b7c88' }} />
            <p className="font-weight-bolder mb-1">O download do relatório começou.</p>
            <p className="mb-3" style={{ color: '#6b7c88' }}>
              Procure <strong>{nomeDoArquivoDia(dados.posto.sigla, dados.data)}</strong> nos seus
              downloads. Se nada baixou, toque no botão abaixo.
            </p>
            <MDBBtn color="success" onClick={baixar} className="btn-block m-0 py-3">
              <MDBIcon icon="file-pdf" className="mr-2" />
              Baixar PDF
            </MDBBtn>
          </MDBCol>
        </MDBRow>
      )}

      {/* No celular o "Voltar" fecha a tela, embaixo de tudo: é saída, não
          ação principal, e em cima roubava a primeira dobra do botão verde. */}
      <MDBRow className="d-md-none mt-4">
        <MDBCol size="12">
          <MDBBtn
            outline
            color="mdb-color"
            onClick={() => history.goBack()}
            className="btn-block m-0"
          >
            <MDBIcon icon="arrow-left" className="mr-2" />
            Voltar
          </MDBBtn>
        </MDBCol>
      </MDBRow>
    </MDBContainer>
  );
}
